import { gatherScreenContext } from "../../../../components/design-tools/left-menu/mcp-chat/context-pills/mcp-chat-context";
import { createAsyncCategoryHandler } from "../category-dispatcher";
import { screenDesignTools } from "./screen-design-tools";

/**
 * KOA-18422 — the screen's EDITABLE SOURCE, for the harness only.
 *
 * The server's picture of a screen arrives one of two ways: the snapshot the
 * browser attaches to a mutating call (`_refreshedContext`), or the one it sends
 * with a chat request. Neither exists when a turn NAVIGATES to a screen and then
 * has to describe it — an apply's screen lane does exactly that, and briefed
 * three populated screens as empty because nothing had written to them yet
 * (session `2bd4547e`). A Designer told the screen is empty cannot use
 * `ui_edit_patch` or `ui_edit_source` either: both match against a source it was
 * never given.
 *
 * Deliberately NOT in the model-facing operation enum (like `find_by_name`).
 * This is the harness asking the canvas what it holds, in the grammar the edit
 * tools patch against — the same `buildEditableScreenSource` output the chat
 * request carries, not a second serialization that could drift from it.
 */
function getEditableScreenSourceForHarness(): { screenId?: string; screenName?: string; editableSource?: string } {
  const context = gatherScreenContext();
  if (!context) return {};
  return {
    screenId: context.screenId,
    screenName: context.screenName,
    editableSource: context.editableSource
  };
}

/**
 * Category handler for screen query operations.
 * Consolidates read-only screen-design-tools handlers for inspecting
 * the component tree, catalog, events, styles, hierarchy, and search.
 *
 * Async because `list_binding_sources` may have to FETCH the workflow behind a
 * managed task screen (KOA-18136): every in-memory source for its parameters is
 * conditional, and returning the inventory without them is indistinguishable
 * from a process that declares none. Every other operation here stays sync and
 * is dispatched unchanged.
 */
export const handleScreenQueryCategory = createAsyncCategoryHandler(
  {
    get_tree: screenDesignTools.sync!.ui_common_get_screen_tree,
    get_info: (input) => {
      // Normalize: accept both kuikaId (single) and kuikaIds (array)
      const ids = input.kuikaIds;
      const kuikaIds = Array.isArray(ids) && ids.length > 0 ? ids : input.kuikaId ? [input.kuikaId] : undefined;
      return screenDesignTools.sync!.ui_common_get_multiple_component_info({ ...input, kuikaIds });
    },
    get_selected: screenDesignTools.sync!.ui_common_get_selected_component,
    list_available: screenDesignTools.sync!.ui_common_list_available_components,
    get_catalog: screenDesignTools.sync!.ui_common_get_component_catalog,
    get_children: screenDesignTools.sync!.ui_common_get_children,
    get_events: screenDesignTools.sync!.ui_common_get_component_events,
    get_styles: screenDesignTools.sync!.ui_common_get_multiple_component_styles,
    get_multiple_info: screenDesignTools.sync!.ui_common_get_multiple_component_info,
    get_hierarchy: screenDesignTools.sync!.ui_common_get_component_hierarchy,
    find_by_name: screenDesignTools.sync!.ui_common_find_components_by_name,
    find_by_type: (input) => {
      const mappedInput = { ...input, componentName: input.componentType || input.componentName };
      return screenDesignTools.sync!.ui_common_find_components_by_name(mappedInput);
    },
    list_all_components: screenDesignTools.sync!.ui_common_list_all_components,
    get_source: () => getEditableScreenSourceForHarness(),
    get_property_value: screenDesignTools.sync!.ui_common_get_property_value,
    search: screenDesignTools.sync!.ui_common_search_components,
    check_completeness: screenDesignTools.sync!.ui_common_check_completeness
  },
  {
    // KOA-18136 — everything on this screen an action parameter can be filled
    // from: the running process's values (task screens only), the screen's own
    // value-holding components, and the result fields of the actions already
    // attached to it. One list, no order of preference.
    list_binding_sources: screenDesignTools.async!.ui_common_list_binding_sources
  }
);
